import { generateUUID } from '@shared/utils/common.util';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

interface TabInfo {
  id: string;
  lastSeen: number; // epoch ms
  formattedTime?: string; // HH:mm:ss format
}

@Injectable({ providedIn: 'root' })
export class TabTrackerService {
  private readonly STORAGE_KEY = 'openTabs';
  private readonly LOGOUT_NEEDED_KEY = 'logout_needed';
  private readonly HEARTBEAT_INTERVAL = 1000; // ms
  private readonly STALE_THRESHOLD = 15000; // ms, increased threshold to account for throttling
  private readonly GRACE_PERIOD = 5000; // ms, additional grace period for background tabs
  private readonly CLOSE_LOGOUT_GRACE_PERIOD = 8000; // ms
  private tabId = `${generateUUID()}`;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private closeLogoutTimer?: ReturnType<typeof setTimeout>;
  private tabCount$ = new BehaviorSubject<number>(0);
  private lastHeartbeat = Date.now();
  private isVisible = true;

  constructor(private router: Router) {
    this.registerTab();
    this.listenToStorage();
    this.setupVisibilityHandling();
    window.addEventListener('pagehide', (event) => {
      if (!event.persisted) {
        this.unregisterTab();
      }
    });
    this.heartbeatTimer = setInterval(() => {
      this.refreshHeartbeat();
    }, this.HEARTBEAT_INTERVAL);
    this.updateTabCount();
  }

  private setupVisibilityHandling(): void {
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;
      if (this.isVisible) {
        this.cancelPendingCloseLogout();
        this.refreshHeartbeat();
      }
    });

    // Handle focus events as additional recovery mechanism
    window.addEventListener('focus', () => {
      this.cancelPendingCloseLogout();
      this.refreshHeartbeat();
    });

    // Handle page show event (back from bfcache)
    window.addEventListener('pageshow', () => {
      this.cancelPendingCloseLogout();
      this.refreshHeartbeat();
    });
  }

  private cancelPendingCloseLogout(): void {
    if (!this.closeLogoutTimer) {
      return;
    }
    clearTimeout(this.closeLogoutTimer);
    this.closeLogoutTimer = undefined;
    localStorage.removeItem(this.LOGOUT_NEEDED_KEY);
    this.registerTab();
    if (!this.heartbeatTimer) {
      this.heartbeatTimer = setInterval(() => {
        this.refreshHeartbeat();
      }, this.HEARTBEAT_INTERVAL);
    }
  }

  shouldLogoutFromPreviousSession(): boolean {
    const flag = localStorage.getItem(this.LOGOUT_NEEDED_KEY);
    if (!flag) return false;
    localStorage.removeItem(this.LOGOUT_NEEDED_KEY);
    const navEntry = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming;
    return navEntry?.type !== 'reload';
  }

  private formatTime(epochMs: number): string {
    const date = new Date(epochMs);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private getTabs(): TabInfo[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      const parsed: TabInfo[] = data ? JSON.parse(data) : [];
      // Filter out stale tabs with improved logic
      const now = Date.now();
      const freshTabs = parsed.filter((t) => {
        const timeSinceLastSeen = now - t.lastSeen;

        // For the current tab, be more lenient
        if (t.id === this.tabId) {
          return true; // Always keep current tab
        }

        // For other tabs, use adaptive threshold
        // If we detect potential throttling (large gap in our own heartbeat),
        // be more lenient with other tabs too
        const heartbeatGap = now - this.lastHeartbeat;
        const adaptiveThreshold =
          heartbeatGap > this.HEARTBEAT_INTERVAL * 3
            ? this.STALE_THRESHOLD + this.GRACE_PERIOD
            : this.STALE_THRESHOLD;

        return timeSinceLastSeen < adaptiveThreshold;
      });

      // Add formatted time to each tab
      freshTabs.forEach((tab) => {
        tab.formattedTime = this.formatTime(tab.lastSeen);
      });

      if (freshTabs.length !== parsed.length) {
        // Persist cleanup
        this.setTabs(freshTabs);
      }
      return freshTabs;
    } catch (e) {
      return [];
    }
  }

  setTabs(tabs: TabInfo[]) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tabs));
    // Immediately propagate the new count within the same tab session
    this.updateTabCount();
  }

  public resetTabs() {
    localStorage.removeItem(this.STORAGE_KEY);
    // Re-register only the current tab so the service continues tracking normally
    this.registerTab();
  }

  registerTab() {
    if (this.router.url.includes('login')) {
      return;
    }
    const now = Date.now();
    const tabs = this.getTabs();
    // Delete the old tabId (in case the browser crashes and you have to open it again)
    const cleanedTabs =
      tabs?.filter((info) => info.id !== this.tabId) || [];
    cleanedTabs.push({
      id: this.tabId,
      formattedTime: this.formatTime(now),
      lastSeen: now,
    });
    this.setTabs(cleanedTabs);
  }
  get isEmpty() {
    return this.getTabs().length === 0;
  }
  private unregisterTab() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    const remaining =
      this.getTabs()?.filter((info) => info.id !== this.tabId) || [];
    if (remaining.length === 0) {
      localStorage.setItem(this.LOGOUT_NEEDED_KEY, 'true');
      this.closeLogoutTimer = setTimeout(() => {
        this.closeLogoutTimer = undefined;
        this.beaconLogout();
      }, this.CLOSE_LOGOUT_GRACE_PERIOD);
    }
    this.setTabs(remaining);
  }

  private beaconLogout(): void {
    const refresh = localStorage.getItem('refresh');
    const sessionName = sessionStorage.getItem('session_name');
    const sessionIdWs = localStorage.getItem('session_id_ws');
    if (!refresh && !sessionIdWs) return;
    const payload = JSON.stringify({
      refresh,
      session_name: sessionName,
      session_id_ws: sessionIdWs,
    });
    // fetch with keepalive guarantees delivery after page close and handles CORS correctly
    fetch(`${environment.baseUrl}/api/logout/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Fallback to sendBeacon if fetch is unavailable
      navigator.sendBeacon(
        `${environment.baseUrl}/api/logout/`,
        new Blob([payload], { type: 'application/json' }),
      );
    });
  }
  private listenToStorage() {
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY) {
        this.updateTabCount();
      }
    });
  }
  private refreshHeartbeat() {
    const tabs = this.getTabs();
    const idx = tabs.findIndex((t) => t.id === this.tabId);
    if (idx > -1) {
      const now = Date.now();
      tabs[idx].lastSeen = now;
      tabs[idx].formattedTime = this.formatTime(now);
      this.lastHeartbeat = now;
      this.setTabs(tabs);
    } else {
      this.registerTab();
    }
  }
  private updateTabCount() {
    const count = this.getTabs().length;
    this.tabCount$.next(count);
  }
  public getOpenTabCount$() {
    return this.tabCount$.asObservable();
  }
}
